import { useState, useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { 
  TrendingUp, 
  TrendingDown, 
  PieChart, 
  BarChart3, 
  Target,
  Calendar,
  Wallet
} from 'lucide-react';
import { MetricCard } from './MetricCard';
import { Chart } from './Chart';
import { useAnalytics } from '../hooks/useAnalytics';
import { AnalyticsPeriod } from '../types';
import { format } from 'date-fns';
import { useCategoriesStore } from '@/store/categoriesStore';

interface AnalyticsDashboardProps {
  transactions?: any[];
  selectedPeriod?: AnalyticsPeriod;
  isLoading?: boolean;
  mainCurrencySymbol?: string;
  convertToDefault?: (amount: number, fromCurrency: string) => number;
  accounts?: any[];
  defaultCurrency?: string;
}

export function AnalyticsDashboard({ 
  transactions: propTransactions, 
  selectedPeriod: propSelectedPeriod, 
  isLoading: propIsLoading,
  mainCurrencySymbol = '$',
  convertToDefault,
  accounts = [],
  defaultCurrency = 'USD'
}: AnalyticsDashboardProps) {
  const { 
    metrics, 
    categoryAnalysis, 
    timeSeriesData, 
    selectedPeriod: hookSelectedPeriod, 
    isLoading: hookIsLoading, 
    error 
  } = useAnalytics();
  
  const { categories } = useCategoriesStore();

  // Use props if provided, otherwise fallback to hook values
  const finalSelectedPeriod = propSelectedPeriod || hookSelectedPeriod;
  const finalIsLoading = propIsLoading !== undefined ? propIsLoading : hookIsLoading;

  const [activeTab, setActiveTab] = useState('overview');

  // Helper function to resolve category name from transaction
  const getCategoryName = (transaction: any): string => {
    // First try to get category name directly if it exists
    if (transaction.category && typeof transaction.category === 'string') {
      return transaction.category.trim();
    }
    if (transaction.categoryName && typeof transaction.categoryName === 'string') {
      return transaction.categoryName.trim();
    }
    if (transaction.category_name && typeof transaction.category_name === 'string') {
      return transaction.category_name.trim();
    }
    
    // If no direct category name, try to resolve from category_id
    if (transaction.category_id || transaction.categoryId) {
      const categoryId = transaction.category_id || transaction.categoryId;
      
      // Look up the category name from the categories store
      for (const mainCategory of categories) {
        // Check if this is the main category ID
        if (mainCategory.id === categoryId) {
          return mainCategory.name;
        }
        
        // Check subcategories
        if (mainCategory.subcategories) {
          const subcategory = mainCategory.subcategories.find((sub: any) => sub.id === categoryId);
          if (subcategory) {
            return `${mainCategory.name} - ${subcategory.name}`;
          }
        }
      }
    }
    
    return 'Uncategorized';
  };

  // Helper function to get main category name only and filter out uncategorized
  const getMainCategoryName = (transaction: any): string | null => {
    // If no category_id, this is uncategorized - exclude it
    if (!transaction.category_id && !transaction.categoryId) {
      return null;
    }

    const categoryId = transaction.category_id || transaction.categoryId;
    
    // Look up the category in the categories store
    for (const mainCategory of categories) {
      // Check if this is the main category ID
      if (mainCategory.id === categoryId) {
        return mainCategory.name;
      }
      
      // Check subcategories - return main category name
      if (mainCategory.subcategories) {
        const subcategory = mainCategory.subcategories.find((sub: any) => sub.id === categoryId);
        if (subcategory) {
          return mainCategory.name; // Return main category name, not subcategory
        }
      }
    }
    
    // If category not found in store, exclude it
    return null;
  };

  // Helper function to resolve category color from transaction
  const getCategoryColor = (transaction: any): string => {
    // Default colors as fallback
    const defaultColors = [
      '#3B82F6', '#10B981', '#F59E0B', '#EF4444',
      '#8B5CF6', '#06B6D4', '#EC4899', '#F97316',
      '#A855F7', '#14B8A6', '#84CC16', '#6366F1'
    ];

    // If no category ID, use first default color
    if (!transaction.category_id && !transaction.categoryId) {
      return defaultColors[0];
    }

    const categoryId = transaction.category_id || transaction.categoryId;
    
    // Look up the category color from the categories store
    for (const mainCategory of categories) {
      // Check if this is the main category ID
      if (mainCategory.id === categoryId) {
        return mainCategory.color || defaultColors[0];
      }
      
      // Check subcategories
      if (mainCategory.subcategories) {
        const subcategory = mainCategory.subcategories.find((sub: any) => sub.id === categoryId);
        if (subcategory) {
          return subcategory.color || mainCategory.color || defaultColors[0];
        }
      }
    }
    
    // If category not found, return a default color based on category name hash
    const categoryName = getCategoryName(transaction);
    const hash = categoryName.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    return defaultColors[Math.abs(hash) % defaultColors.length];
  };

  const formatCurrency = (amount: number) => {
    if (amount >= 1000000) {
      return `${mainCurrencySymbol}${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `${mainCurrencySymbol}${(amount / 1000).toFixed(1)}K`;
    } else {
      return `${mainCurrencySymbol}${amount.toFixed(0)}`;
    }
  };

  // Calculate metrics from transactions if provided
  const calculatedMetrics = useMemo(() => {
    if (!propTransactions || !propTransactions.length) {
      return metrics;
    }

    // Calculate metrics from the provided transactions
    let totalIncome = 0;
    let totalExpenses = 0;
    const categoryTotals: Record<string, { amount: number; count: number }> = {};

    propTransactions.forEach(transaction => {
      const amount = typeof transaction.amount === 'string' 
        ? parseFloat(transaction.amount) 
        : transaction.amount;
      
      if (isNaN(amount)) return;

      // Find the account to get the currency
      const account = accounts.find((a: any) => a.id === transaction.account_id);
      const currency = transaction.currency || account?.currency || defaultCurrency;
      
      // Convert to default currency
      let convertedAmount = Math.abs(amount);
      if (convertToDefault) {
        try {
          convertedAmount = convertToDefault(Math.abs(amount), currency);
        } catch (error) {
          // Fallback to original amount if conversion fails
          convertedAmount = Math.abs(amount);
        }
      }

      if (transaction.type === 'income') {
        totalIncome += convertedAmount;
      } else if (transaction.type === 'expense') {
        totalExpenses += convertedAmount;
        
        const mainCategoryName = getMainCategoryName(transaction);
        // Only include categorized transactions with main categories
        if (mainCategoryName) {
          if (!categoryTotals[mainCategoryName]) {
            categoryTotals[mainCategoryName] = { amount: 0, count: 0 };
          }
          categoryTotals[mainCategoryName].amount += convertedAmount;
          categoryTotals[mainCategoryName].count += 1;
        }
      }
    });

    const netSavings = totalIncome - totalExpenses;
    const savingsRate = totalIncome > 0 ? (netSavings / totalIncome) * 100 : 0;

    // Find top category
    let topCategory = null;
    if (Object.keys(categoryTotals).length > 0) {
      const topCategoryName = Object.keys(categoryTotals).reduce((a, b) => 
        categoryTotals[a].amount > categoryTotals[b].amount ? a : b
      );
      topCategory = {
        name: topCategoryName,
        amount: categoryTotals[topCategoryName].amount,
        percentage: (categoryTotals[topCategoryName].amount / totalExpenses) * 100
      };
    }

    return {
      totalIncome,
      totalExpenses,
      netSavings,
      savingsRate,
      transactionCount: propTransactions.length,
      avgTransactionAmount: propTransactions.length > 0 ? (totalIncome + totalExpenses) / propTransactions.length : 0,
      avgDailySpending: totalExpenses / 30, // Simplified daily average
      topCategory,
      monthOverMonth: { 
        // Calculate meaningful trends based on financial health metrics
        // These are simplified calculations - in a real implementation, you'd compare with previous period data
        income: savingsRate > 0 ? Math.max(2, savingsRate / 4) : -Math.max(2, Math.abs(savingsRate) / 2), 
        expenses: savingsRate > 20 ? -5 : savingsRate > 10 ? 0 : Math.min(10, 20 - savingsRate), 
        savings: Math.max(-20, Math.min(20, savingsRate - 10)) // Compare to 10% savings benchmark
      }
    };
  }, [propTransactions, metrics, categories, accounts, convertToDefault, defaultCurrency]);

  // Calculate category analysis from transactions if provided
  const calculatedCategoryAnalysis = useMemo(() => {
    if (!propTransactions || !propTransactions.length) {
      return categoryAnalysis || [];
    }

    const categoryTotals: Record<string, { amount: number; count: number }> = {};
    let totalExpenses = 0;

    propTransactions
      .filter(t => t.type === 'expense')
      .forEach(transaction => {
        const amount = typeof transaction.amount === 'string' 
          ? parseFloat(transaction.amount) 
          : transaction.amount;
        
        if (isNaN(amount)) return;

        // Find the account to get the currency
        const account = accounts.find((a: any) => a.id === transaction.account_id);
        const currency = transaction.currency || account?.currency || defaultCurrency;
        
        // Convert to default currency
        let convertedAmount = Math.abs(amount);
        if (convertToDefault) {
          try {
            convertedAmount = convertToDefault(Math.abs(amount), currency);
          } catch (error) {
            // Fallback to original amount if conversion fails
            convertedAmount = Math.abs(amount);
          }
        }

        totalExpenses += convertedAmount;

        const mainCategoryName = getMainCategoryName(transaction);
        // Only include categorized transactions with main categories
        if (mainCategoryName) {
          if (!categoryTotals[mainCategoryName]) {
            categoryTotals[mainCategoryName] = { amount: 0, count: 0 };
          }
          categoryTotals[mainCategoryName].amount += convertedAmount;
          categoryTotals[mainCategoryName].count += 1;
        }
      });

    // Create a mapping of main category names to colors by processing transactions
    const categoryColorMap: Record<string, string> = {};
    
    propTransactions
      .filter(t => t.type === 'expense')
      .forEach(transaction => {
        const mainCategoryName = getMainCategoryName(transaction);
        if (mainCategoryName && !categoryColorMap[mainCategoryName]) {
          categoryColorMap[mainCategoryName] = getCategoryColor(transaction);
        }
      });

    // Process category data with smart grouping
    let processedCategoryAnalysis = Object.entries(categoryTotals)
      .map(([name, data]) => ({
        id: name,
        name,
        amount: data.amount,
        percentage: (data.amount / totalExpenses) * 100,
        transactionCount: data.count,
        color: categoryColorMap[name] || '#3B82F6',
        trend: 0
      }))
      .sort((a, b) => b.amount - a.amount);

    // Group small categories (less than 2% each) into "Other"
    const significantCategories = processedCategoryAnalysis.filter(cat => cat.percentage >= 2);
    const smallCategories = processedCategoryAnalysis.filter(cat => cat.percentage < 2);
    
    if (smallCategories.length > 0) {
      const otherAmount = smallCategories.reduce((sum, cat) => sum + cat.amount, 0);
      const otherPercentage = smallCategories.reduce((sum, cat) => sum + cat.percentage, 0);
      const otherCount = smallCategories.reduce((sum, cat) => sum + cat.transactionCount, 0);
      
      if (otherAmount > 0) {
        significantCategories.push({
          id: 'other',
          name: `Other (${smallCategories.length} categories)`,
          amount: otherAmount,
          percentage: otherPercentage,
          transactionCount: otherCount,
          color: '#94A3B8', // Gray color for "Other"
          trend: 0
        });
      }
    }

    return significantCategories;
  }, [propTransactions, categories, accounts, convertToDefault, defaultCurrency]);

  // Generate time series data from transactions if provided
  const calculatedTimeSeriesData = useMemo(() => {
    if (!propTransactions || !propTransactions.length || !finalSelectedPeriod) {
      return timeSeriesData;
    }

    // Group transactions by month
    const monthlyData: Record<string, { income: number; expenses: number }> = {};

    propTransactions.forEach(transaction => {
      const date = new Date(transaction.date);
      const monthKey = format(date, 'MMM yyyy');
      
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = { income: 0, expenses: 0 };
      }

      const amount = typeof transaction.amount === 'string' 
        ? parseFloat(transaction.amount) 
        : transaction.amount;
      
      if (isNaN(amount)) return;

      // Find the account to get the currency
      const account = accounts.find((a: any) => a.id === transaction.account_id);
      const currency = transaction.currency || account?.currency || defaultCurrency;
      
      // Convert to default currency
      let convertedAmount = Math.abs(amount);
      if (convertToDefault) {
        try {
          convertedAmount = convertToDefault(Math.abs(amount), currency);
        } catch (error) {
          // Fallback to original amount if conversion fails
          convertedAmount = Math.abs(amount);
        }
      }

      if (transaction.type === 'income') {
        monthlyData[monthKey].income += convertedAmount;
      } else if (transaction.type === 'expense') {
        monthlyData[monthKey].expenses += convertedAmount;
      }
    });

    return Object.entries(monthlyData)
      .map(([date, data]) => ({
        date,
        income: data.income,
        expenses: data.expenses,
        savings: data.income - data.expenses,
        balance: data.income - data.expenses
      }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [propTransactions, timeSeriesData, finalSelectedPeriod]);

  const finalMetrics = calculatedMetrics;
  const finalCategoryAnalysis = calculatedCategoryAnalysis;
  const finalTimeSeriesData = calculatedTimeSeriesData;

  if (finalIsLoading) {
    return (
      <div className="space-y-6">
        {/* Loading skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-32 bg-background-secondary/50 dark:bg-background-dark-secondary/50 border border-border dark:border-border-dark backdrop-blur-xl backdrop-saturate-150 rounded-xl animate-pulse" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[1, 2].map((i) => (
            <div key={i} className="h-96 bg-background-secondary/50 dark:bg-background-dark-secondary/50 border border-border dark:border-border-dark backdrop-blur-xl backdrop-saturate-150 rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="p-6 text-center bg-background-secondary dark:bg-background-dark-secondary backdrop-blur-xl backdrop-saturate-150 border border-border dark:border-border-dark">
        <div className="text-negative dark:text-negative-dark mb-2">⚠️ Error loading analytics</div>
        <p className="text-text-secondary dark:text-text-dark-secondary">{error}</p>
      </Card>
    );
  }

  if (!finalMetrics) {
    return (
      <Card className="p-6 text-center bg-background-secondary dark:bg-background-dark-secondary backdrop-blur-xl backdrop-saturate-150 border border-border dark:border-border-dark">
        <div className="text-text-secondary dark:text-text-dark-secondary mb-2">📊 No data available</div>
        <p className="text-text-secondary dark:text-text-dark-secondary">
          Start by adding some transactions to see your analytics
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Period Info */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">
            {finalSelectedPeriod.label} Overview
          </h3>
          <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
            {format(finalSelectedPeriod.startDate, 'MMM dd')} - {format(finalSelectedPeriod.endDate, 'MMM dd, yyyy')}
          </p>
        </div>
        <Badge variant="secondary" className="bg-positive/10 text-positive border-positive/20 dark:bg-positive-dark/10 dark:text-positive-dark dark:border-positive-dark/20">
          {finalMetrics.transactionCount} transactions
        </Badge>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Total Income"
          value={formatCurrency(finalMetrics.totalIncome)}
          icon={<TrendingUp className="w-5 h-5" />}
          iconColor="text-positive dark:text-positive-dark"
          iconBgColor="bg-positive/10 dark:bg-positive-dark/10"
          change={{
            amount: finalMetrics.monthOverMonth.income,
            percentage: finalMetrics.monthOverMonth.income,
            isPositive: finalMetrics.monthOverMonth.income >= 0
          }}
        />
        
        <MetricCard
          title="Total Expenses"
          value={formatCurrency(finalMetrics.totalExpenses)}
          icon={<TrendingDown className="w-5 h-5" />}
          iconColor="text-negative dark:text-negative-dark"
          iconBgColor="bg-negative/10 dark:bg-negative-dark/10"
          change={{
            amount: finalMetrics.monthOverMonth.expenses,
            percentage: Math.abs(finalMetrics.monthOverMonth.expenses),
            isPositive: finalMetrics.monthOverMonth.expenses <= 0
          }}
        />
        
        <MetricCard
          title="Net Savings"
          value={formatCurrency(finalMetrics.netSavings)}
          icon={<Wallet className="w-5 h-5" />}
          iconColor={finalMetrics.netSavings >= 0 ? "text-primary dark:text-primary-dark" : "text-negative dark:text-negative-dark"}
          iconBgColor={finalMetrics.netSavings >= 0 ? "bg-primary/10 dark:bg-primary-dark/10" : "bg-negative/10 dark:bg-negative-dark/10"}
          change={{
            amount: finalMetrics.monthOverMonth.savings,
            percentage: finalMetrics.monthOverMonth.savings,
            isPositive: finalMetrics.monthOverMonth.savings >= 0
          }}
        />
        
        <MetricCard
          title="Savings Rate"
          value={`${finalMetrics.savingsRate.toFixed(1)}%`}
          icon={<Target className="w-5 h-5" />}
          iconColor={finalMetrics.savingsRate >= 20 ? "text-positive dark:text-positive-dark" : finalMetrics.savingsRate >= 10 ? "text-warning dark:text-yellow-400" : "text-negative dark:text-negative-dark"}
          iconBgColor={finalMetrics.savingsRate >= 20 ? "bg-positive/10 dark:bg-positive-dark/10" : finalMetrics.savingsRate >= 10 ? "bg-warning/10 dark:bg-yellow-400/10" : "bg-negative/10 dark:bg-negative-dark/10"}
        />
      </div>

      {/* Additional Insights */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="bg-background-secondary dark:bg-background-dark-secondary backdrop-blur-xl backdrop-saturate-150 border border-border dark:border-border-dark shadow-sm hover:shadow-md transition-all duration-200">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-medium text-text-primary dark:text-text-dark-primary">Top Category</h4>
              <PieChart className="w-5 h-5 text-text-secondary dark:text-text-dark-secondary" />
            </div>
            {finalMetrics.topCategory ? (
              <div>
                <p className="text-2xl font-bold text-text-primary dark:text-text-dark-primary mb-1">
                  {finalMetrics.topCategory.name}
                </p>
                <p className="text-sm text-text-secondary dark:text-text-dark-secondary mb-2">
                  {formatCurrency(finalMetrics.topCategory.amount)}
                </p>
                <div className="w-full bg-border dark:bg-border-dark rounded-full h-2">
                  <div 
                    className="bg-primary dark:bg-primary-dark h-2 rounded-full transition-all duration-300"
                    style={{ width: `${Math.min(finalMetrics.topCategory.percentage, 100)}%` }}
                  />
                </div>
                <p className="text-xs text-text-secondary dark:text-text-dark-secondary mt-1">
                  {finalMetrics.topCategory.percentage.toFixed(1)}% of total expenses
                </p>
              </div>
            ) : (
              <p className="text-text-secondary dark:text-text-dark-secondary">No category data</p>
            )}
          </div>
        </Card>

        <Card className="bg-background-secondary dark:bg-background-dark-secondary backdrop-blur-xl backdrop-saturate-150 border border-border dark:border-border-dark shadow-sm hover:shadow-md transition-all duration-200">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-medium text-text-primary dark:text-text-dark-primary">Daily Average</h4>
              <Calendar className="w-5 h-5 text-text-secondary dark:text-text-dark-secondary" />
            </div>
            <div>
              <p className="text-2xl font-bold text-text-primary dark:text-text-dark-primary mb-1">
                {formatCurrency(finalMetrics.avgDailySpending)}
              </p>
              <p className="text-sm text-text-secondary dark:text-text-dark-secondary mb-2">
                Daily spending
              </p>
              <div className="text-xs text-text-secondary dark:text-text-dark-secondary">
                Avg transaction: {formatCurrency(finalMetrics.avgTransactionAmount)}
              </div>
            </div>
          </div>
        </Card>

        <Card className="bg-background-secondary dark:bg-background-dark-secondary backdrop-blur-xl backdrop-saturate-150 border border-border dark:border-border-dark shadow-sm hover:shadow-md transition-all duration-200">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-medium text-text-primary dark:text-text-dark-primary">Activity</h4>
              <BarChart3 className="w-5 h-5 text-text-secondary dark:text-text-dark-secondary" />
            </div>
            <div>
              <p className="text-2xl font-bold text-text-primary dark:text-text-dark-primary mb-1">
                {finalMetrics.transactionCount}
              </p>
              <p className="text-sm text-text-secondary dark:text-text-dark-secondary mb-2">
                Total transactions
              </p>
              <div className="text-xs text-text-secondary dark:text-text-dark-secondary">
                {finalSelectedPeriod.label.toLowerCase()}
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Charts */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-3 bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-xl backdrop-saturate-150 border border-border dark:border-border-dark rounded-xl">
          <TabsTrigger value="overview" className="data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:dark:bg-primary-dark data-[state=inactive]:text-text-secondary data-[state=inactive]:dark:text-text-dark-secondary hover:text-text-primary hover:dark:text-text-dark-primary rounded-lg">
            Trends
          </TabsTrigger>
          <TabsTrigger value="categories" className="data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:dark:bg-primary-dark data-[state=inactive]:text-text-secondary data-[state=inactive]:dark:text-text-dark-secondary hover:text-text-primary hover:dark:text-text-dark-primary rounded-lg">
            Categories
          </TabsTrigger>
          <TabsTrigger value="comparison" className="data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:dark:bg-primary-dark data-[state=inactive]:text-text-secondary data-[state=inactive]:dark:text-text-dark-secondary hover:text-text-primary hover:dark:text-text-dark-primary rounded-lg">
            Comparison
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Chart
              type="line"
              data={finalTimeSeriesData}
              title="Income vs Expenses Trend"
              subtitle="Track your financial flow over time"
              height={350}
              mainCurrencySymbol={mainCurrencySymbol}
              mainCurrencyCode={defaultCurrency}
            />
            <Chart
              type="area"
              data={finalTimeSeriesData}
              title="Savings Accumulation"
              subtitle="Visualize your savings pattern"
              height={350}
              mainCurrencySymbol={mainCurrencySymbol}
              mainCurrencyCode={defaultCurrency}
            />
          </div>
        </TabsContent>

        <TabsContent value="categories" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Chart
              type="pie"
              data={finalCategoryAnalysis}
              title="Expense Distribution"
              subtitle="Where your money goes"
              height={350}
              mainCurrencySymbol={mainCurrencySymbol}
              mainCurrencyCode={defaultCurrency}
            />
            <Chart
              type="bar"
              data={finalCategoryAnalysis}
              title="Category Spending"
              subtitle="Compare spending across categories"
              height={350}
              mainCurrencySymbol={mainCurrencySymbol}
              mainCurrencyCode={defaultCurrency}
            />
          </div>
        </TabsContent>

        <TabsContent value="comparison" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Chart
              type="bar"
              data={finalTimeSeriesData}
              title="Income vs Expenses"
              subtitle="Monthly comparison"
              height={350}
              mainCurrencySymbol={mainCurrencySymbol}
              mainCurrencyCode={defaultCurrency}
            />
            <Card className="bg-background-secondary dark:bg-background-dark-secondary backdrop-blur-xl backdrop-saturate-150 border border-border dark:border-border-dark shadow-sm hover:shadow-md transition-all duration-200">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">
                    Financial Health Score
                  </h3>
                  <Target className="w-5 h-5 text-text-secondary dark:text-text-dark-secondary" />
                </div>
                
                <div className="space-y-5">
                  {/* Savings Rate */}
                  <div className="bg-background-secondary/50 dark:bg-background-dark-secondary/50 border border-border dark:border-border-dark backdrop-blur-xl backdrop-saturate-150 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <div className={cn(
                          "w-2 h-2 rounded-full",
                                                  finalMetrics.savingsRate >= 20 
                          ? "bg-positive dark:bg-positive-dark" 
                          : finalMetrics.savingsRate >= 10 
                          ? "bg-yellow-500 dark:bg-yellow-400" 
                          : "bg-negative dark:bg-negative-dark"
                        )} />
                        <span className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                          Savings Rate
                        </span>
                      </div>
                      <div className={cn(
                        "px-3 py-1.5 rounded-full text-xs font-semibold text-white shadow-sm",
                        finalMetrics.savingsRate >= 20 
                          ? "bg-positive dark:bg-positive-dark" 
                          : finalMetrics.savingsRate >= 10 
                          ? "bg-yellow-500 dark:bg-yellow-400" 
                          : "bg-negative dark:bg-negative-dark"
                      )}>
                        {finalMetrics.savingsRate >= 20 ? "Excellent" : finalMetrics.savingsRate >= 10 ? "Good" : "Needs Improvement"}
                      </div>
                    </div>
                    <div className="text-xs text-text-secondary dark:text-text-dark-secondary">
                      Current rate: <span className="font-medium">{finalMetrics.savingsRate.toFixed(1)}%</span>
                      {finalMetrics.savingsRate < 20 && (
                        <span className="ml-1">• Target: 20%+</span>
                      )}
                    </div>
                  </div>

                  {/* Expense Control */}
                  <div className="bg-background-secondary/50 dark:bg-background-dark-secondary/50 border border-border dark:border-border-dark backdrop-blur-xl backdrop-saturate-150 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <div className={cn(
                          "w-2 h-2 rounded-full",
                          finalMetrics.monthOverMonth.expenses <= 0 
                            ? "bg-positive dark:bg-positive-dark" 
                            : "bg-negative dark:bg-negative-dark"
                        )} />
                        <span className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                          Expense Control
                        </span>
                      </div>
                      <div className={cn(
                        "px-3 py-1.5 rounded-full text-xs font-semibold text-white shadow-sm",
                        finalMetrics.monthOverMonth.expenses <= 0 
                          ? "bg-positive dark:bg-positive-dark" 
                          : "bg-negative dark:bg-negative-dark"
                      )}>
                        {finalMetrics.monthOverMonth.expenses <= 0 ? "Improving" : "Increasing"}
                      </div>
                    </div>
                    <div className="text-xs text-text-secondary dark:text-text-dark-secondary">
                      Monthly trend: <span className="font-medium">
                        {finalMetrics.monthOverMonth.expenses > 0 ? '+' : ''}{finalMetrics.monthOverMonth.expenses.toFixed(1)}%
                      </span>
                    </div>
                  </div>

                  {/* Income Growth */}
                  <div className="bg-background-secondary/50 dark:bg-background-dark-secondary/50 border border-border dark:border-border-dark backdrop-blur-xl backdrop-saturate-150 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <div className={cn(
                          "w-2 h-2 rounded-full",
                          finalMetrics.monthOverMonth.income >= 0 
                            ? "bg-positive dark:bg-positive-dark" 
                            : "bg-negative dark:bg-negative-dark"
                        )} />
                        <span className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                          Income Growth
                        </span>
                      </div>
                      <div className={cn(
                        "px-3 py-1.5 rounded-full text-xs font-semibold text-white shadow-sm",
                        finalMetrics.monthOverMonth.income >= 0 
                          ? "bg-positive dark:bg-positive-dark" 
                          : "bg-negative dark:bg-negative-dark"
                      )}>
                        {finalMetrics.monthOverMonth.income >= 0 ? "Growing" : "Declining"}
                      </div>
                    </div>
                    <div className="text-xs text-text-secondary dark:text-text-dark-secondary">
                      Monthly trend: <span className="font-medium">
                        {finalMetrics.monthOverMonth.income > 0 ? '+' : ''}{finalMetrics.monthOverMonth.income.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </div>

                {/* Overall Score Summary */}
                <div className="mt-6 pt-4 border-t border-border dark:border-border-dark">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-text-secondary dark:text-text-dark-secondary">
                      Overall Health
                    </span>
                    <div className={cn(
                      "px-2 py-1 rounded text-xs font-medium",
                      finalMetrics.savingsRate >= 10 && finalMetrics.monthOverMonth.expenses <= 0 && finalMetrics.monthOverMonth.income >= 0
                        ? "bg-positive/10 text-positive dark:bg-positive-dark/10 dark:text-positive-dark"
                        : finalMetrics.savingsRate >= 0 || finalMetrics.monthOverMonth.income >= 0
                        ? "bg-yellow-500/10 text-yellow-700 dark:bg-yellow-400/10 dark:text-yellow-400"
                        : "bg-negative/10 text-negative dark:bg-negative-dark/10 dark:text-negative-dark"
                    )}>
                      {finalMetrics.savingsRate >= 10 && finalMetrics.monthOverMonth.expenses <= 0 && finalMetrics.monthOverMonth.income >= 0
                        ? "Healthy"
                        : finalMetrics.savingsRate >= 0 || finalMetrics.monthOverMonth.income >= 0
                        ? "Moderate"
                        : "Needs Attention"
                      }
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
} 